import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AgentDashboardTab =
  | 'overview'
  | 'chat'
  | 'reports'
  | 'articles'
  | 'tickets'

interface AgentChatState {
  activeSessionId: string | null
  recentSessionIds: string[]
  activeDashboardTab: AgentDashboardTab
  setActiveSessionId: (sessionId: string | null) => void
  setActiveDashboardTab: (tab: AgentDashboardTab) => void
  touchSession: (sessionId: string) => void
  clearActiveSession: () => void
}

const MAX_RECENT_SESSIONS = 50

export const useAgentChatStore = create<AgentChatState>()(
  persist(
    set => ({
      activeSessionId: null,
      recentSessionIds: [],
      activeDashboardTab: 'overview',
      setActiveSessionId: sessionId => {
        if (!sessionId) {
          set({ activeSessionId: null })
          return
        }

        set(state => {
          const withoutCurrent = state.recentSessionIds.filter(
            id => id !== sessionId
          )
          const updated = [sessionId, ...withoutCurrent].slice(
            0,
            MAX_RECENT_SESSIONS
          )

          return {
            activeSessionId: sessionId,
            recentSessionIds: updated,
          }
        })
      },
      setActiveDashboardTab: tab => {
        set({ activeDashboardTab: tab })
      },
      touchSession: sessionId => {
        set(state => {
          const withoutCurrent = state.recentSessionIds.filter(
            id => id !== sessionId
          )
          const updated = [sessionId, ...withoutCurrent].slice(
            0,
            MAX_RECENT_SESSIONS
          )

          return {
            recentSessionIds: updated,
          }
        })
      },
      clearActiveSession: () => {
        set({ activeSessionId: null })
      },
    }),
    {
      name: 'agent-chat-store',
      partialize: state => ({
        activeSessionId: state.activeSessionId,
        recentSessionIds: state.recentSessionIds,
        activeDashboardTab: state.activeDashboardTab,
      }),
    }
  )
)

export const useAgentChatState = () => {
  const store = useAgentChatStore()
  return {
    activeSessionId: store.activeSessionId,
    recentSessionIds: store.recentSessionIds,
    activeDashboardTab: store.activeDashboardTab,
    setActiveSessionId: store.setActiveSessionId,
    setActiveDashboardTab: store.setActiveDashboardTab,
    touchSession: store.touchSession,
    clearActiveSession: store.clearActiveSession,
  }
}
